/**
 * Inbox summary stats for shared ops notifications.
 */

import React from "react";
import { Box, Card, CardContent, Stack, Typography, Skeleton } from "@mui/material";
import { getCategoryIconComponent } from "../utils/icons.js";
import { CATEGORY_ACCENT } from "../utils/taxonomy.js";

const STAT_CARDS = [
  { key: "actionRequired", label: "Action Required", shortLabel: "Action", category: "action_required" },
  { key: "updates", label: "Updates", shortLabel: "Updates", category: "updates" },
  { key: "read", label: "Read", shortLabel: "Read", category: "read" },
  { key: "system", label: "System", shortLabel: "System", category: "system" },
  { key: "total", label: "All Activity", shortLabel: "All", category: "all" },
];

const resolveValues = (stats) => {
  const byCategory = stats?.byCategory || {};
  const actionRequired = stats?.actionRequired ?? byCategory.action_required ?? 0;
  return {
    actionRequired,
    updates: Math.max(0, (stats?.unread || 0) - actionRequired),
    read: stats?.read || 0,
    system: byCategory.system || 0,
    total: stats?.total || 0,
  };
};

const NotificationInboxStats = ({ stats, loading, compact = false }) => {
  const values = resolveValues(stats);

  if (compact) {
    return (
      <Stack
        direction="row"
        spacing={1}
        sx={{
          overflowX: "auto",
          pb: 0.5,
          WebkitOverflowScrolling: "touch",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {STAT_CARDS.map((card) => {
          const Icon = getCategoryIconComponent(card.category);
          const accent = CATEGORY_ACCENT[card.category] || CATEGORY_ACCENT.all;
          return (
            <Box
              key={card.key}
              sx={{
                flex: "0 0 auto",
                minWidth: 84,
                border: "1.5px solid",
                borderColor: "divider",
                borderRadius: 3,
                p: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 0.5,
              }}
            >
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: `${accent}18`,
                  color: accent,
                }}
              >
                <Icon sx={{ fontSize: 14 }} />
              </Box>
              {loading && !stats ? (
                <Skeleton width={28} height={22} />
              ) : (
                <Typography variant="h6" fontWeight={800} color="text.primary" sx={{ lineHeight: 1 }}>
                  {Number(values[card.key] || 0).toLocaleString()}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                {card.shortLabel}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    );
  }

  if (loading && !stats) {
    return (
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        {STAT_CARDS.map((card) => (
          <Card
            key={card.key}
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              flex: "1 1 160px",
              minWidth: 160,
            }}
          >
            <CardContent>
              <Skeleton width="60%" />
              <Skeleton width="40%" height={36} />
            </CardContent>
          </Card>
        ))}
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
      {STAT_CARDS.map((card) => {
        const Icon = getCategoryIconComponent(card.category);
        const accent = CATEGORY_ACCENT[card.category] || CATEGORY_ACCENT.all;
        return (
          <Card
            key={card.key}
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              flex: "1 1 160px",
              minWidth: 160,
            }}
          >
            <CardContent sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: `${accent}18`,
                  color: accent,
                  flexShrink: 0,
                }}
              >
                <Icon fontSize="small" />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {card.label}
                </Typography>
                <Typography variant="h5" fontWeight={700} color="text.primary">
                  {Number(values[card.key] || 0).toLocaleString()}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );
};

export default NotificationInboxStats;
